import { z } from "zod";
import { getIndexAbove, getIndexBetween } from "@tldraw/utils";
import type { TLPageId, TLPage, TLShape } from "@tldraw/tlschema";
import type { OperationContext } from "./agent-operation-context";

const pageId = z.string().regex(/^page:[\w-]{1,100}$/);
export const pageOperations = [
  z
    .object({
      type: z.literal("add_page"),
      id: pageId,
      name: z.string().trim().min(1).max(200),
      beforePageId: pageId.nullable().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("update_page"),
      id: pageId,
      name: z.string().trim().min(1).max(200).optional(),
      beforePageId: pageId.nullable().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("move_sheet"),
      id: z.string().regex(/^shape:[\w-]{1,100}$/),
      pageId,
    })
    .strict(),
] as const;
const operation = z.discriminatedUnion("type", pageOperations);
export function applyPageOperation(input: unknown, c: OperationContext) {
  const parsed = operation.safeParse(input);
  if (!parsed.success) return false;
  const op = parsed.data;
  const pages = [...c.txn.values()]
    .filter((r): r is TLPage => r.typeName === "page")
    .sort((a, b) => (a.index < b.index ? -1 : 1));
  if (op.type === "move_sheet") {
    const sheet = c.editable(op.id);
    if (sheet.type !== "sheet" || !pages.some((p) => p.id === op.pageId))
      throw new Error("Choose a sheet and an existing destination page");
    const siblings = [...c.txn.values()].filter(
      (r): r is TLShape => r.typeName === "shape" && r.parentId === op.pageId,
    );
    c.set({
      ...sheet,
      parentId: op.pageId as TLPageId,
      index: getIndexAbove(
        siblings
          .map((s) => s.index)
          .sort()
          .at(-1),
      ),
    });
    const descendants = new Set<string>([sheet.id]);
    for (let n = 0; n < 32; n++) {
      const before = descendants.size;
      for (const r of c.txn.values())
        if (r.typeName === "shape" && descendants.has(r.parentId))
          descendants.add(r.id);
      if (before === descendants.size) break;
    }
    const threads = new Set<string>();
    for (const r of c.txn.values())
      if (
        r.typeName === "comment-thread" &&
        r.anchor.type === "shape" &&
        descendants.has(r.anchor.shapeId)
      ) {
        threads.add(r.id);
        c.set({ ...r, pageId: op.pageId as TLPageId });
      }
    for (const r of c.txn.values())
      if (r.typeName === "comment" && threads.has(r.threadId))
        c.set({ ...r, pageId: op.pageId as TLPageId });
    return true;
  }
  const current = c.txn.get(op.id);
  if (op.type === "add_page" ? Boolean(current) : current?.typeName !== "page")
    throw new Error(
      op.type === "add_page" ? "Page ID already exists" : "Page not found",
    );
  const others = pages.filter((p) => p.id !== op.id);
  const before = op.beforePageId
    ? others.findIndex((p) => p.id === op.beforePageId)
    : others.length;
  if (before < 0) throw new Error("Page ordering target not found");
  const index =
    op.type === "update_page" &&
    op.beforePageId === undefined &&
    current?.typeName === "page"
      ? current.index
      : getIndexBetween(others[before - 1]?.index, others[before]?.index);
  c.set({
    ...(current?.typeName === "page"
      ? current
      : { typeName: "page", id: op.id as TLPageId, meta: {} }),
    name: op.name ?? (current as TLPage).name,
    index,
  });
  return true;
}
